'use client';

import { useTransition } from 'react';

import { cn } from '@ps/ui/utils';
import { SmilePlus } from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import { useMyMessageReactions } from '../../hooks/useMyMessageReactions';
import { useToggleMessageReaction } from '../../hooks/useToggleMessageReaction';
import type { ExtendedCommunityChannelMessage } from '../../types/ExtendedCommunityChannelMessage';
import type { MessageInteractionMode } from '../../types/MessageInteractionMode';
import type { MessageReaction } from '../../types/MessageReaction';
import { EmojiPickerPopover } from '../common/EmojiPickerPopover';

interface Props {
  message: ExtendedCommunityChannelMessage;
  currentUserId: string | null | undefined;
  /** Gates interaction: `readonly` hides the add-reaction button and makes
   *  existing chips non-clickable (a non-member). Defaults to full interaction. */
  mode?: MessageInteractionMode;
}

/**
 * Per-bubble reaction row — chips for each existing reaction
 * (clickable to toggle the current user's own reaction off), plus a
 * "+smile" button that opens the emoji picker for new reactions.
 *
 * The chips' "is this mine?" highlight comes from a Firestore
 * subscription on the message's `reactions` subcollection scoped to
 * the current user — so the highlight flips immediately when the
 * server confirms a toggle (no manual optimistic state to keep in
 * sync).
 *
 * Reaction `count` updates ride through the existing message-doc
 * subscription — the server bumps the count on the message doc
 * itself, which is what we render here.
 */
export function MessageReactionBar({
  message,
  currentUserId,
  mode = 'normal',
}: Props) {
  const { creatorId } = useCommunityContext();
  // A deleted message keeps its existing reaction chips (read-only) but loses
  // the add-reaction picker — matches upstream, which only gates the picker on
  // `!message.deleted` while still rendering the chips.
  const readonly = mode === 'readonly' || message.deleted;
  const myCodes = useMyMessageReactions(
    creatorId,
    message.channelId,
    message.id,
    currentUserId,
  );
  const toggle = useToggleMessageReaction({
    channelId: message.channelId,
    messageId: message.id,
  });
  const [pending, startTransition] = useTransition();

  const entries = Object.entries(message.reactions).filter(
    ([, stat]) => (stat.count ?? 0) > 0,
  );

  // Read-only with nothing to show → render nothing (no empty add-button row).
  if (readonly && entries.length === 0) return null;

  const handleToggle = (reaction: MessageReaction) => {
    if (pending) return;
    startTransition(async () => {
      try {
        await toggle(reaction);
      } catch (error) {
        console.error('toggle reaction failed', error);
      }
    });
  };

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {!readonly ? (
        <EmojiPickerPopover
          trigger={
            <button
              type="button"
              aria-label="Add reaction"
              className="hover:text-content-1 text-content-3"
            >
              <SmilePlus className="h-4 w-4" aria-hidden />
            </button>
          }
          onSelect={handleToggle}
        />
      ) : null}
      {entries.map(([key, stat]) => {
        const mine = myCodes.has(stat.emojiCode);
        return (
          <button
            key={key}
            type="button"
            disabled={pending}
            onClick={
              readonly
                ? undefined
                : () =>
                    handleToggle({
                      emoji: stat.emoji,
                      emojiCode: stat.emojiCode,
                    })
            }
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs transition disabled:cursor-not-allowed disabled:opacity-50',
              // Read-only / deleted-message chips stay full-opacity (just
              // non-clickable), matching upstream — only an in-flight toggle
              // (`pending`) dims via `disabled:`.
              readonly && 'pointer-events-none',
              mine
                ? 'border-brand bg-brand/15 text-brand-hover'
                : 'border-line-strong bg-surface-1 text-content-2 hover:border-raise/20',
            )}
          >
            <span aria-hidden>{stat.emoji}</span>
            <span className="tabular-nums">{stat.count}</span>
          </button>
        );
      })}
    </div>
  );
}
